import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
  Delete as DeleteIcon,
  Clear as CleanIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  actionContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
    justifyContent: "center",
    // Screen reader only class for accessibility
    "& .sr-only": {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: 0,
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: 0,
    },
  },
  actionButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? theme.palette.grey[700]
        : theme.palette.grey[100],
    },
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? theme.palette.primary.dark + "20"
        : theme.palette.primary.light + "20",
    },
  },
  blockButton: {
    color: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? theme.palette.warning.dark + "20"
        : theme.palette.warning.light + "20",
    },
  },
  unblockButton: {
    color: theme.palette.success.main,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? theme.palette.success.dark + "20"
        : theme.palette.success.light + "20",
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? theme.palette.error.dark + "20"
        : theme.palette.error.light + "20",
    },
  },
  cleanButton: {
    color: theme.palette.info.main,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? theme.palette.info.dark + "20"
        : theme.palette.info.light + "20",
    },
  },
  disabledButton: {
    color: theme.palette.text.disabled,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const ActionButtons = ({
  company,
  onEdit,
  onDelete,
  onToggleStatus,
  onCleanFiles,
  disabled = false,
}) => {
  const classes = useStyles();

  // Refs for keyboard navigation
  const containerRef = useRef(null);
  const editButtonRef = useRef(null);
  const blockButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);
  const cleanButtonRef = useRef(null);

  // Check if company ID is 1 (protected company)
  const isProtectedCompany = company?.id === 1;

  // Enhanced keyboard navigation within action buttons
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!containerRef.current?.contains(document.activeElement)) return;

      const buttons = [
        editButtonRef.current,
        blockButtonRef.current,
        deleteButtonRef.current,
        cleanButtonRef.current,
      ].filter(btn => btn && !btn.disabled);

      const currentIndex = buttons.findIndex(btn => btn === document.activeElement);

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % buttons.length;
        const nextButton = buttons[nextIndex];
        if (nextButton) {
          nextButton.focus();
          const announcement = nextButton.getAttribute('aria-label');
          announceToScreenReader(`Navegando para: ${announcement}`);
        }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
        const prevButton = buttons[prevIndex];
        if (prevButton) {
          prevButton.focus();
          const announcement = prevButton.getAttribute('aria-label');
          announceToScreenReader(`Navegando para: ${announcement}`);
        }
      } else if (event.key === 'Home') {
        event.preventDefault();
        const firstButton = buttons[0];
        if (firstButton) {
          firstButton.focus();
          announceToScreenReader('Navegando para o primeiro botão de ação');
        }
      } else if (event.key === 'End') {
        event.preventDefault();
        const lastButton = buttons[buttons.length - 1];
        if (lastButton) {
          lastButton.focus();
          announceToScreenReader('Navegando para o último botão de ação');
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => {
        container.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [disabled, isProtectedCompany]);

  // Helper function to announce messages to screen readers
  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  };

  // Determine if buttons should be disabled
  const isEditDisabled = disabled;
  const isDeleteDisabled = disabled || isProtectedCompany;
  const isBlockDisabled = disabled || isProtectedCompany;
  const isCleanDisabled = disabled || isProtectedCompany;

  // Generate tooltip messages
  const getTooltipMessage = (action) => {
    if (disabled) return `${action} temporariamente indisponível`;
    if (isProtectedCompany) {
      switch (action) {
        case "Bloquear":
        case "Desbloquear":
          return "🔒 Empresa principal protegida - Não é possível alterar status";
        case "Excluir":
          return "🛡️ Empresa principal protegida - Não é possível excluir";
        case "Limpar Arquivos":
          return "🗂️ Empresa principal protegida - Não é possível limpar arquivos";
        default:
          return action;
      }
    }

    // Enhanced tooltips for normal operations
    switch (action) {
      case "Editar":
        return "✏️ Editar informações da empresa";
      case "Bloquear":
        return "🔒 Bloquear acesso da empresa ao sistema";
      case "Desbloquear":
        return "🔓 Restaurar acesso da empresa ao sistema";
      case "Excluir":
        return "🗑️ Excluir empresa permanentemente";
      case "Limpar Arquivos":
        return "🧹 Limpar todos os arquivos da empresa";
      default:
        return action;
    }
  };

  const handleEdit = () => {
    if (!isEditDisabled && onEdit) {
      onEdit(company);
    }
  };

  const handleDelete = () => {
    if (!isDeleteDisabled && onDelete) {
      onDelete(company);
    }
  };

  const handleToggleStatus = () => {
    if (!isBlockDisabled && onToggleStatus) {
      onToggleStatus(company);
    }
  };

  const handleCleanFiles = () => {
    if (!isCleanDisabled && onCleanFiles) {
      onCleanFiles(company);
    }
  };

  return (
    <div
      ref={containerRef}
      className={classes.actionContainer}
      role="group"
      aria-label={`Ações para empresa ${company?.name || company?.id}`}
    >
      {/* Edit Button */}
      <Tooltip title={getTooltipMessage("Editar")}>
        <span>
          <IconButton
            ref={editButtonRef}
            size="small"
            onClick={handleEdit}
            disabled={isEditDisabled}
            className={`${classes.actionButton} ${isEditDisabled ? classes.disabledButton : classes.editButton
              }`}
            aria-label={`Editar empresa ${company?.name || company?.id}`}
            aria-describedby={`edit-tooltip-${company?.id}`}
            role="button"
            tabIndex={0}
          >
            <EditIcon fontSize="small" aria-hidden="true" />
          </IconButton>
          <div id={`edit-tooltip-${company?.id}`} className="sr-only">
            {getTooltipMessage("Editar")}
          </div>
        </span>
      </Tooltip>

      {/* Block/Unblock Button */}
      <Tooltip title={getTooltipMessage(company?.status ? "Bloquear" : "Desbloquear")}>
        <span>
          <IconButton
            ref={blockButtonRef}
            size="small"
            onClick={handleToggleStatus}
            disabled={isBlockDisabled}
            className={`${classes.actionButton} ${isBlockDisabled
              ? classes.disabledButton
              : company?.status
                ? classes.blockButton
                : classes.unblockButton
              }`}
            aria-label={`${company?.status ? "Bloquear" : "Desbloquear"} empresa ${company?.name || company?.id}`}
            aria-describedby={`block-tooltip-${company?.id}`}
            aria-pressed={!company?.status}
            role="button"
            tabIndex={0}
          >
            {company?.status ? (
              <BlockIcon fontSize="small" aria-hidden="true" />
            ) : (
              <UnblockIcon fontSize="small" aria-hidden="true" />
            )}
          </IconButton>
          <div id={`block-tooltip-${company?.id}`} className="sr-only">
            {getTooltipMessage(company?.status ? "Bloquear" : "Desbloquear")}
          </div>
        </span>
      </Tooltip>

      {/* Delete Button */}
      <Tooltip title={getTooltipMessage("Excluir")}>
        <span>
          <IconButton
            ref={deleteButtonRef}
            size="small"
            onClick={handleDelete}
            disabled={isDeleteDisabled}
            className={`${classes.actionButton} ${isDeleteDisabled ? classes.disabledButton : classes.deleteButton
              }`}
            aria-label={`Excluir empresa ${company?.name || company?.id}`}
            aria-describedby={`delete-tooltip-${company?.id}`}
            role="button"
            tabIndex={0}
          >
            <DeleteIcon fontSize="small" aria-hidden="true" />
          </IconButton>
          <div id={`delete-tooltip-${company?.id}`} className="sr-only">
            {getTooltipMessage("Excluir")}
          </div>
        </span>
      </Tooltip>

      {/* Clean Files Button */}
      <Tooltip title={getTooltipMessage("Limpar Arquivos")}>
        <span>
          <IconButton
            ref={cleanButtonRef}
            size="small"
            onClick={handleCleanFiles}
            disabled={isCleanDisabled}
            className={`${classes.actionButton} ${isCleanDisabled ? classes.disabledButton : classes.cleanButton
              }`}
            aria-label={`Limpar arquivos da empresa ${company?.name || company?.id}`}
            aria-describedby={`clean-tooltip-${company?.id}`}
            role="button"
            tabIndex={0}
          >
            <CleanIcon fontSize="small" aria-hidden="true" />
          </IconButton>
          <div id={`clean-tooltip-${company?.id}`} className="sr-only">
            {getTooltipMessage("Limpar Arquivos")}
          </div>
        </span>
      </Tooltip>
    </div>
  );
};

ActionButtons.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    status: PropTypes.bool,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onCleanFiles: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ActionButtons;